import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Grid } from '@material-ui/core';
import StorageIcon from '@mui/icons-material/Storage';
import BackupIcon from '@mui/icons-material/Backup';
import RadioCard from '../../../utils/RadioCard';
import SelectRackSnapshot from './SelectRackSnapshot';
import { defaultRackState } from '../../../pages/DefaultRackState';

export function getDefaultRackState() {
  return JSON.parse(JSON.stringify(defaultRackState));
}

export function parseSnapshotRackValues(rack, snapshotRackDetails) {
  const modifiedRackValues = JSON.parse(JSON.stringify(rack)); // safe deep clone
  Object.keys(snapshotRackDetails.lrus).forEach((lruKey) => {
    if (snapshotRackDetails.lrus[lruKey].enabled) {
      modifiedRackValues.rack.lrus[lruKey] = {
        ...modifiedRackValues.rack.lrus[lruKey],
        ...snapshotRackDetails.lrus[lruKey]
      };
    }
  });

  modifiedRackValues.systemType = snapshotRackDetails.system_type;
  modifiedRackValues.rack.gateway = {
    ...modifiedRackValues.rack.gateway,
    ...snapshotRackDetails.gateway
  };

  return modifiedRackValues;
}

export function updateRackWithSnapshot(prev, selectedSnapshot, rackCreationMethod) {
  if (rackCreationMethod !== 'snapshot') {
    if (prev.rack.snapshotDetails !== null) {
      return {
        ...prev,
        rack: {
          ...getDefaultRackState().rack,
          snapshotDetails: null
        }
      };
    }
    return prev;
  }

  if (!selectedSnapshot) return prev;

  const freshBase = { ...prev, rack: getDefaultRackState().rack };
  const parsedRackValues = parseSnapshotRackValues(freshBase, selectedSnapshot.rack_details);

  const next = {
    ...parsedRackValues,
    rack: {
      ...parsedRackValues.rack,
      snapshotDetails: { name: selectedSnapshot.name }
    }
  };

  // Avoid unnecessary updates
  if (JSON.stringify(prev) === JSON.stringify(next)) return prev;

  return next;
}

RackCreationMethod.propTypes = {
  rack: PropTypes.object,
  updateRack: PropTypes.func,
  selectedSnapshot: PropTypes.object,
  setSelectedSnapshot: PropTypes.func,
  handleSelectedMethodValidation: PropTypes.func
};

export default function RackCreationMethod({
  rack,
  updateRack,
  selectedSnapshot,
  setSelectedSnapshot,
  handleSelectedMethodValidation
}) {
  const rackCreationMethod = rack?.rackCreationMethod || 'custom';
  const setRackCreationMethod = (value) => {
    updateRack((prev) => ({
      ...prev,
      rackCreationMethod: value
    }));
  };

  useEffect(() => {
    if (rackCreationMethod === 'snapshot') {
      handleSelectedMethodValidation(!!selectedSnapshot);
    } else {
      handleSelectedMethodValidation(true);
    }
  }, [rackCreationMethod, selectedSnapshot, handleSelectedMethodValidation]);

  useEffect(() => {
    updateRack((prevRack) => {
      const nextRack = updateRackWithSnapshot(prevRack, selectedSnapshot, rackCreationMethod);
      return {
        ...prevRack,
        ...nextRack
      };
    });
  }, [rackCreationMethod, selectedSnapshot, updateRack]);

  return (
    <Grid container spacing={2} alignItems="stretch">
      <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
        <RadioCard
          value="custom"
          selectedValue={rackCreationMethod}
          onChange={setRackCreationMethod}
          title="Custom"
          description="Creates a VRack from the beginning by specifying the virtual parts list, resource sizes and loadables."
          icon={<StorageIcon fontSize="small" />}
        />
      </Grid>

      <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
        <RadioCard
          value="snapshot"
          selectedValue={rackCreationMethod}
          onChange={setRackCreationMethod}
          title="Clone From Existing Snapshot"
          description="Creates a VRack by using an image of another VRack with the identical virtual parts list, resource sizes and loadables. The use of Snapshots will significantly decrease provisioning time"
          icon={<BackupIcon fontSize="small" />}
          children={
            rackCreationMethod === 'snapshot' && (
              <Box sx={{ mt: 1 }}>
                <SelectRackSnapshot
                  selectedSnapshot={selectedSnapshot}
                  setSelectedSnapshot={setSelectedSnapshot}
                />
              </Box>
            )
          }
        />
      </Grid>
    </Grid>
  );
}
